"use client";
import * as NextIamge from "next/image";
import Masonry from "react-masonry-css";
import { useState, useEffect } from "react";

const blogCfEndpoint = process.env.NEXT_PUBLIC_BLOG_CF_ENDPOINT;

// 더 다양한 비율의 이미지 예시
const images = [
  {
    src: `${blogCfEndpoint}/files/IMG_2003.jpeg`,
    alt: "IMG_2003",
  },
  {
    src: `${blogCfEndpoint}/files/IMG_4836.jpeg`,
    alt: "IMG_4836",
  },
  {
    src: `${blogCfEndpoint}/files/IMG_6395.webp`,
    alt: "IMG_6395",
  },
  {
    src: `${blogCfEndpoint}/files/IMG_0835.webp`,
    alt: "IMG_0835",
  },
  {
    src: `${blogCfEndpoint}/files/000012.webp`,
    alt: "000012",
  },
  {
    src: `${blogCfEndpoint}/files/IMG_1628.webp`,
    alt: "IMG_1628",
  },
  {
    src: `${blogCfEndpoint}/files/IMG_6064.webp`,
    alt: "IMG_6064",
  },
  {
    src: `${blogCfEndpoint}/files/IMG_7883.webp`,
    alt: "IMG_7883",
  },
  {
    src: `${blogCfEndpoint}/files/IMG_7490.webp`,
    alt: "IMG_7490",
  },
];

// 이미지 비율을 확인하는 커스텀 훅
function useImageDimensions(src: string) {
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
    aspectRatio: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!src) return;

    const img = new Image();
    img.onload = () => {
      setDimensions({
        width: img.width,
        height: img.height,
        aspectRatio: img.width / img.height,
      });
      setIsLoading(false);
    };
    img.src = src;

    return () => {
      img.onload = null;
    };
  }, [src]);

  return { ...dimensions, isLoading };
}

// 이미지 아이템 컴포넌트
function MasonryImageItem({ image }: { image: { src: string; alt: string } }) {
  const { width, height, aspectRatio, isLoading } = useImageDimensions(
    image.src
  );

  // 이미지 로딩 완료 상태 추가
  const [imageLoaded, setImageLoaded] = useState(false);
  const isGif = image.src.toLowerCase().endsWith(".gif");

  // 비율에 따른 패딩 값 계산 - 이미지 비율 보존
  const paddingTop = isLoading ? "75%" : `${(height / width) * 100}%`;

  // 세로로 매우 긴 이미지 제한
  const maxHeight = aspectRatio < 0.5 ? "120vh" : "none";

  return (
    <div key={image.src} className="mb-4">
      <div
        className="relative overflow-hidden rounded-lg bg-gray-50 dark:bg-gray-400/20 select-none"
        style={{
          paddingTop,
          maxHeight,
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* 개선된 스켈레톤 로더 - 더 자연스러운 움직임 */}
        {(isLoading || !imageLoaded) && (
          <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-700">
            <div className="w-full h-full opacity-30 animate-pulse-slow bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%]"></div>
          </div>
        )}

        {/* 이미지가 로드될 때만 표시 */}
        {!isLoading && (
          <NextIamge.default
            src={image.src}
            alt={image.alt}
            fill
            priority
            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out pointer-events-none ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            style={{
              objectFit: "cover",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
            }}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            onLoadingComplete={() => {
              setTimeout(() => setImageLoaded(true), 300);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default function Moments() {
  // CSS 스타일을 추가하기 위한 useEffect
  useEffect(() => {
    // 더 자연스러운 느린 펄스 애니메이션을 위한 커스텀 CSS 추가
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes pulse-slow {
        0%, 100% {
          background-position: 0% 50%;
        }
        50% {
          background-position: 100% 50%;
        }
      }
      .animate-pulse-slow {
        animation: pulse-slow 2.5s ease-in-out infinite;
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // 반응형 열 설정
  const breakpointColumnsObj = {
    default: 3, // 기본 3열
    1024: 2, // 1024px 이하에서 2열
    640: 1, // 640px 이하에서 1열
  };

  return (
    <main className="max-w-screen-lg mx-auto p-4">
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="flex -ml-4 w-auto"
        columnClassName="pl-4"
      >
        {images.map((image) => (
          <MasonryImageItem key={image.src} image={image} />
        ))}
      </Masonry>
    </main>
  );
}
